import { useEffect, useRef } from "react";
import { useExplanationSidebar } from "@coffee/contexts";
import { EvaluatedClause } from "@coffee/data-access/api-types";
import styled from "@emotion/styled";
import { MarkdownViewer } from "./MarkdownViewer";

const ClauseContainer = styled.div<{ selected?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: ${({ theme }) => theme.roundness.sm};
  border: 2px solid
    ${({ theme, selected }) =>
      selected
        ? theme.colors.background.client
        : theme.colors.component.border[1]};
  justify-content: center;
  align-items: stretch;
  box-sizing: border-box;
`;

const CitationHeader = styled.div`
  font-weight: bold;
  padding-bottom: ${({ theme }) => theme.spacings.xs};
`;

// Minus 2px from border-radius to account for the border width of the container
const ClauseHeader = styled.div`
  padding: ${({ theme }) => theme.spacings["lg"]};
  width: 100%;

  background-color: ${({ theme }) => theme.colors.background[1]};
  border-top-left-radius: calc(${({ theme }) => theme.roundness.sm} - 2px);
  border-top-right-radius: calc(${({ theme }) => theme.roundness.sm} - 2px);
`;

// Minus 2px from border-radius to account for the border width of the container
const ClauseBody = styled.div`
  padding: ${({ theme }) => theme.spacings["lg"]};
  width: 100%;
  font-weight: bold;
  border-bottom-left-radius: calc(${({ theme }) => theme.roundness.sm} - 2px);
  border-bottom-right-radius: calc(${({ theme }) => theme.roundness.sm} - 2px);
`;

type EvaluatedClauseProps = {
  clause: EvaluatedClause;
};

export const Clause = ({ clause }: EvaluatedClauseProps) => {
  const { isOpen, selectedClauseId } = useExplanationSidebar();
  const ref = useRef<HTMLDivElement>(null);

  const formatCitationText = (text: string) => {
    // Remove citation header if it's at the start, possibly prefixed by hashtags or bold markdown
    const header = clause.citation_header.trim();
    const patterns = [
      `**${header}**`,
      `__${header}__`,
      `*${header}*`,
      `_${header}_`,
      `# ${header}`,
      `## ${header}`,
      `### ${header}`,
      `#### ${header}`,
      `##### ${header}`,
      `###### ${header}`,
      header,
    ];
    for (const pattern of patterns) {
      if (text.trimStart().startsWith(pattern)) {
        text = text.trimStart().slice(pattern.length).trim();
        break;
      }
    }
    // remove "table:" prefix if it exists
    if (text.startsWith("table:")) {
      text = text.slice(6).trim();
    }
    return text.trim();
  };

  useEffect(() => {
    if (!isOpen || !selectedClauseId) {
      return;
    }

    // wait 0.3 seconds to ensure the sidebar is open
    setTimeout(() => {
      if (clause.citation_header === selectedClauseId && ref.current) {
        const el = ref.current;
        const rect = el.getBoundingClientRect();
        // ant-drawer-body is the class for the element representing the available height
        const drawerBody = el?.closest(
          ".ant-drawer-body"
        ) as HTMLElement | null;
        const viewportHeight = drawerBody?.offsetHeight || window.innerHeight;
        // If the element is taller than the viewport, scroll to the top; otherwise, center
        const block = rect.height > viewportHeight ? "start" : "center";
        el.scrollIntoView({ behavior: "smooth", block });
      }
    }, 300);
  }, [clause.citation_header, selectedClauseId, isOpen]);

  return (
    <ClauseContainer
      ref={ref}
      selected={clause.citation_header === selectedClauseId}
    >
      <ClauseHeader>
        <CitationHeader>{clause.citation_header}</CitationHeader>
        <MarkdownViewer
          content={formatCitationText(clause.citation_text)}
        ></MarkdownViewer>
      </ClauseHeader>
      <ClauseBody>{clause.reasoning}</ClauseBody>
    </ClauseContainer>
  );
};
